import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  updateFormData,
  calculateInterest
} from '../redux/actions/investmentForm';
import { 
  selectFormData, 
  selectCurrency,
  selectCalculatedInterest
} from '../redux/selectors/investmentFormSelectors';
import useInvestmentProductRate from './useInvestmentProductRate';

/**
 * Custom hook to handle investment duration terms logic
 */
export const useInvestmentDurationTerms = (form: any, tenantId?: number, investRateId?: number, data?: any) => {
  const dispatch = useDispatch();
  
  // Get data from Redux
  const formData = useSelector(selectFormData);
  const calculatedInterest = useSelector(selectCalculatedInterest);
  const currencyCode = useSelector(selectCurrency) || 'NGN';

  // Initialize form with values from redux when component mounts
  useEffect(() => {
    if (formData) {
      form.setFieldsValue({
        tenure: formData.tenure,
        interest_rate: formData.interest_rate,
        interest_repayment_type: formData.interest_repayment_type,
        number_of_days_in_year: formData.number_of_days_in_year || 365
      });
    }
  }, [form, formData]);

  // Calculate interest on mount
  useEffect(() => {
    dispatch(calculateInterest());
  }, [dispatch]);

  // Handle tenure change
  const handleTenureChange = (value: number | null) => {
    if (value === null || value === undefined) return;
    
    // Ensure we have a number
    const numValue = Number(value);
    
    // Update Redux state
    dispatch(updateFormData({ tenure: numValue }));
    
    // Set form value
    form.setFieldsValue({ tenure: numValue });
    
    // Delay interest calculation to avoid UI jank
    setTimeout(() => {
      dispatch(calculateInterest());
    }, 500);
  };

  // Handle days in year change
  const handleDaysInYearChange = (value: number) => {
    dispatch(updateFormData({ number_of_days_in_year: value }));
    form.setFieldsValue({ number_of_days_in_year: value });
  };

  // Handle interest repayment type change
  const handleInterestRepaymentTypeChange = (e: any) => {
    const value = e.target.value;
    dispatch(updateFormData({ interest_repayment_type: value }));
    dispatch(calculateInterest());
  };

  // Get investment product rate
  const { data: investmentProductRate } = useInvestmentProductRate({
    investRateId: investRateId || 0,
    enteredPrincipalAmount: form.getFieldValue('principal_amount') ?? 0,
    tenure: form.getFieldValue('tenure') ?? 0,
    tenantId: tenantId || 0,
    depositType:
      data?.investment_product_details[0]?.deposit_type === "RecurringDeposit"
        ? 4
        : 3,
  });

  // Set interest rate from API
  useEffect(() => {
    if (investmentProductRate?.data) {
      const interestRate = investmentProductRate?.data?.depositProductInterestRate?.interest;
      form.setFieldsValue({ interest_rate: interestRate });
      dispatch(updateFormData({ interest_rate: interestRate }));
    }
  }, [investmentProductRate, form, dispatch]);

  return {
    calculatedInterest,
    currencyCode,
    handleTenureChange,
    handleDaysInYearChange,
    handleInterestRepaymentTypeChange,
    hasCalculatedInterest: !!calculatedInterest.interest_calculated
  };
};

export default useInvestmentDurationTerms;